/**
 * Autocomplete Search Component
 *
 * Inline header search with autocomplete suggestions from Vespa.
 * Shows recent searches, trending searches, and autocomplete suggestions.
 * Redirects to /a/search?q=query on Enter or suggestion click.
 *
 * Search Page Integration:
 * - On search pages (/a/search or /<locale>/a/search), the search bar automatically
 *   expands and displays the current query from the URL
 * - Dispatches 'search:query-changed' custom event when query changes
 *
 * Example: Listen to search query changes in your app proxy code:
 *
 *   document.addEventListener('search:query-changed', (event) => {
 *     const query = event.detail.query;
 *     // Update search results based on new query
 *     updateSearchResults(query);
 *   });
 */

import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const RECENT_SEARCHES_KEY = 'theme:recent-searches';
const MAX_RECENT_SEARCHES = 12;
const AUTOCOMPLETE_DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

// Leading-button icons: an X that closes the overlay, or a back arrow
// (used on /a/search, where there is no other way back to the site).
const LEAD_ICON_CLOSE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="22" height="22"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 5 5 15M5 5l10 10"/></svg>';
const LEAD_ICON_BACK = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="22" height="22"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12.5 16 6.5 10l6-6M6.5 10H17"/></svg>';

class AutocompleteSearchComponent extends Component {
  requiredRefs = ['searchInput', 'searchDropdown', 'clearButton', 'searchForm', 'leadButton'];

  constructor() {
    super();
    this.recentSearches = [];
    this.trendingSearches = [];
    this.autocompleteSuggestions = [];
    this.isExpanded = false;
    this.selectedIndex = -1;
    this.abortController = null;

    // Bind methods
    this.handleInput = debounce(this.handleInput.bind(this), AUTOCOMPLETE_DEBOUNCE_MS);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleComponentClick = this.handleComponentClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleLead = this.handleLead.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Load recent searches from localStorage
    this.loadRecentSearches();

    // Set up event listeners
    this.refs.searchInput.addEventListener('input', this.handleInput);
    this.refs.searchInput.addEventListener('focus', this.handleFocus);
    this.refs.searchInput.addEventListener('click', this.handleClick);
    // Also listen on the component itself to catch clicks that don't reach the input
    this.addEventListener('click', this.handleComponentClick);
    this.refs.searchInput.addEventListener('keydown', this.handleKeyDown);
    this.refs.searchForm.addEventListener('submit', this.handleSubmit);
    this.refs.clearButton.addEventListener('click', this.handleClear);
    this.refs.leadButton.addEventListener('click', this.handleLead);
    // Use capture phase to catch the click before it bubbles
    document.addEventListener('click', this.handleClickOutside, true);

    // Configure the leading button (X = close overlay, ← = back to site)
    this.setupLeadButton();

    // Load trending searches on init
    this.loadTrendingSearches();

    // Check if we're on the search page and expand by default
    this.checkSearchPageAndExpand();

    // Set up responsive placeholder switching
    this.setupResponsivePlaceholder();
  }

  /**
   * Set up responsive placeholder switching between mobile and desktop
   */
  setupResponsivePlaceholder() {
    const input = this.refs.searchInput;
    const mobilePlaceholder = input.dataset.placeholderMobile;
    const desktopPlaceholder = input.dataset.placeholderDesktop;

    if (!mobilePlaceholder || !desktopPlaceholder) return;

    // Set placeholder once based on viewport, no resize listener needed
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    input.placeholder = isDesktop ? desktopPlaceholder : mobilePlaceholder;
  }

  /**
   * Detect whether we're on the search proxy page.
   * Matches /a/search, /<locale>/a/search, and /<locale-region>/a/search
   * (e.g. /de-de/a/search). Shopify locale paths are lowercase, and a
   * trailing slash may be present, so the match is case-insensitive and
   * tolerates a trailing slash.
   */
  isSearchPage() {
    const path = window.location.pathname;
    return /^(\/[a-z]{2}(-[a-z]{2})?)?\/a\/search\/?$/i.test(path);
  }

  /**
   * Check if we're on the search page and expand search bar
   */
  checkSearchPageAndExpand() {
    if (this.isSearchPage()) {
      // Get query from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get('q');

      if (query) {
        this.refs.searchInput.value = query;
        this.refs.clearButton.style.display = 'block';
      }

      // Expand the search bar but don't show dropdown
      this.isExpanded = true;
      this.classList.add('is-expanded');
      this.refs.searchDropdown.hidden = true;
      this.refs.searchInput.setAttribute('aria-expanded', 'false');

      // Mark the document so the header chrome (announcement bar) can
      // collapse — on /a/search the search bar acts as the page header.
      document.documentElement.classList.add('fy-search-results-page');

      // Position the mobile dropdown below the now-fixed bar so it lands
      // correctly when the user later focuses the input and starts typing.
      void this.offsetHeight;
      this.positionMobileDropdown();
    }
  }

  /**
   * Get trending searches from data attribute or Vespa
   */
  getTrendingSearchesOverride() {
    const override = this.dataset.trendingSearches;
    if (override && override.trim().length > 0) {
      return override.split(',').map(s => s.trim()).filter(s => s.length > 0);
    }
    return null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up event listeners
    this.refs.searchInput.removeEventListener('input', this.handleInput);
    this.refs.searchInput.removeEventListener('focus', this.handleFocus);
    this.refs.searchInput.removeEventListener('click', this.handleClick);
    this.removeEventListener('click', this.handleComponentClick);
    this.refs.searchInput.removeEventListener('keydown', this.handleKeyDown);
    this.refs.searchForm.removeEventListener('submit', this.handleSubmit);
    this.refs.clearButton.removeEventListener('click', this.handleClear);
    this.refs.leadButton.removeEventListener('click', this.handleLead);
    document.removeEventListener('click', this.handleClickOutside, true);

    // Cancel any pending requests
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Load recent searches from localStorage
   */
  loadRecentSearches() {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        this.recentSearches = JSON.parse(stored);
      }
    } catch (error) {
      console.error('[AutocompleteSearch] Failed to load recent searches:', error);
      this.recentSearches = [];
    }
  }

  /**
   * Save recent searches to localStorage
   */
  saveRecentSearches() {
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(this.recentSearches));
    } catch (error) {
      console.error('[AutocompleteSearch] Failed to save recent searches:', error);
    }
  }

  /**
   * Add a search query to recent searches
   */
  addToRecentSearches(query) {
    if (!query || query.trim().length === 0) return;

    const normalizedQuery = query.trim().toLowerCase();

    // Remove if already exists (will re-add at front)
    this.recentSearches = this.recentSearches.filter(
      item => item.query.toLowerCase() !== normalizedQuery
    );

    // Add to front
    this.recentSearches.unshift({
      query: query.trim(),
      timestamp: Date.now()
    });

    // Limit to max items
    if (this.recentSearches.length > MAX_RECENT_SEARCHES) {
      this.recentSearches = this.recentSearches.slice(0, MAX_RECENT_SEARCHES);
    }

    this.saveRecentSearches();
  }

  /**
   * Remove a specific recent search
   */
  removeRecentSearch(query) {
    this.recentSearches = this.recentSearches.filter(
      item => item.query !== query
    );
    this.saveRecentSearches();
    this.renderDropdown();
  }

  /**
   * Clear all recent searches
   */
  clearAllRecentSearches() {
    this.recentSearches = [];
    this.saveRecentSearches();
    this.renderDropdown();
  }

  /**
   * Load trending searches from override or Vespa
   */
  async loadTrendingSearches() {
    // Check if there's an override from theme settings
    const override = this.getTrendingSearchesOverride();
    if (override) {
      this.trendingSearches = override.map(query => ({
        query,
        popularity: 0
      }));
      return;
    }

    // Otherwise, load from Vespa
    try {
      const url = 'https://www.iamfy.co/apps/vespa-search/search?query=sddocname:query&ranking.profile=popularity_rank&restrict=query&hits=10';

      const response = await fetch(url);
      const data = await response.json();

      if (data.root?.children) {
        this.trendingSearches = data.root.children
          .map(child => ({
            query: child.fields.query?.replace(/<\/?hi>/g, ''),
            popularity: child.fields.popularity || 0
          }))
          .filter(item => item.query);
      }
    } catch (error) {
      console.error('[AutocompleteSearch] Failed to load trending searches:', error);
      this.trendingSearches = [];
    }
  }

  /**
   * Fetch autocomplete suggestions from the autocomplete API
   */
  async fetchAutocompleteSuggestions(query) {
    if (!query || query.length < MIN_QUERY_LENGTH) {
      this.autocompleteSuggestions = [];
      return;
    }

    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    try {
      const encodedQuery = encodeURIComponent(query);
      const url = `/apps/chat-agent/api/autocomplete?q=${encodedQuery}`;

      const response = await fetch(url, {
        signal: this.abortController.signal
      });

      const data = await response.json();

      if (data.suggestions && data.suggestions.length > 0) {
        this.autocompleteSuggestions = data.suggestions
          .map(suggestion => ({
            query: suggestion,
            highlightedQuery: this.escapeHtml(suggestion),
            popularity: 0
          }))
          .filter(item => item.query);
      } else {
        this.autocompleteSuggestions = [];
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[AutocompleteSearch] Failed to fetch suggestions:', error);
      }
      this.autocompleteSuggestions = [];
    }
  }

  /**
   * Handle input event
   */
  async handleInput(event) {
    const query = event.target.value;

    // Show/hide clear button
    this.refs.clearButton.style.display = query.length > 0 ? 'block' : 'none';

    // Dispatch custom event for search page to listen to
    this.dispatchSearchQueryEvent(query);

    // Fetch autocomplete suggestions
    await this.fetchAutocompleteSuggestions(query);

    // Render dropdown
    this.renderDropdown();
  }

  /**
   * Dispatch a custom event when search query changes
   * The app proxy search page can listen to this event
   */
  dispatchSearchQueryEvent(query) {
    const event = new CustomEvent('search:query-changed', {
      detail: { query },
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(event);
  }

  /**
   * Handle click event (especially for mobile)
   */
  handleClick(event) {
    // Stop event from bubbling to document (prevents handleClickOutside from firing)
    event.stopPropagation();

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    // Immediately expand on click, before focus event fires
    if (!this.isExpanded) {
      this.expand();
      this.renderDropdown();
    } else if (isDesktop && this.refs.searchDropdown.hidden) {
      // Already expanded but dropdown hidden - show it (desktop only)
      this.refs.searchDropdown.hidden = false;
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
      this.renderDropdown();
    }
  }

  /**
   * Handle click event on the component itself
   * This catches clicks that don't reach the input element
   */
  async handleComponentClick(event) {
    // Stop event from propagating to document
    event.stopPropagation();

    // If clicking on the input wrapper or input itself, expand and focus
    if (!this.isExpanded) {
      this.expand();

      // Check if there's a value in the input and fetch suggestions
      const query = this.refs.searchInput.value.trim();

      // Show/hide clear button based on input value
      this.refs.clearButton.style.display = query.length > 0 ? 'block' : 'none';

      if (query.length >= MIN_QUERY_LENGTH) {
        await this.fetchAutocompleteSuggestions(query);
      }

      this.renderDropdown();
      // Focus the input if it's not already focused
      if (document.activeElement !== this.refs.searchInput) {
        this.refs.searchInput.focus();
      }
    }
  }

  /**
   * Handle focus event
   */
  async handleFocus() {
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    if (!this.isExpanded) {
      this.expand();
    } else if (isDesktop && this.refs.searchDropdown.hidden) {
      // Already expanded but dropdown hidden - show it (desktop only)
      this.refs.searchDropdown.hidden = false;
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
    }

    // Check if there's a value in the input and fetch suggestions
    const query = this.refs.searchInput.value.trim();

    // Show/hide clear button based on input value
    this.refs.clearButton.style.display = query.length > 0 ? 'block' : 'none';

    if (query.length >= MIN_QUERY_LENGTH) {
      await this.fetchAutocompleteSuggestions(query);
    }

    this.renderDropdown();
  }

  /**
   * Handle keydown event
   */
  handleKeyDown(event) {
    const items = this.refs.searchDropdown.querySelectorAll('[role="option"]');

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.updateSelectedItem(items);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelectedItem(items);
        break;

      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          const item = items[this.selectedIndex];
          const query = item.dataset.query;
          const suggestionType = item.dataset.suggestionType;
          const position = item.dataset.position ? parseInt(item.dataset.position, 10) : null;
          this.navigateToSearch(query, { source: suggestionType || 'suggestion', position });
        } else {
          this.handleSubmit(event);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.collapse();
        this.refs.searchInput.blur();
        break;
    }
  }

  /**
   * Update selected item visually
   */
  updateSelectedItem(items) {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add('is-selected');
        item.setAttribute('aria-selected', 'true');
        this.refs.searchInput.setAttribute('aria-activedescendant', item.id);
      } else {
        item.classList.remove('is-selected');
        item.setAttribute('aria-selected', 'false');
      }
    });

    // Update input value to match selection
    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      const query = items[this.selectedIndex].dataset.query;
      this.refs.searchInput.value = query;
    }
  }

  /**
   * Handle form submit
   */
  handleSubmit(event) {
    event.preventDefault();
    const query = this.refs.searchInput.value.trim();

    if (query.length > 0) {
      this.navigateToSearch(query);
    }
  }

  /**
   * Handle Clear link click — empties the input only.
   * This does NOT dismiss the search overlay; closing is the leading
   * button's job. Keeping search open lets the user retype immediately.
   */
  handleClear(event) {
    event.preventDefault();
    event.stopPropagation();

    this.refs.searchInput.value = '';
    this.refs.clearButton.style.display = 'none';
    this.autocompleteSuggestions = [];
    this.selectedIndex = -1;

    // Dispatch event for cleared search
    this.dispatchSearchQueryEvent('');

    // Keep the overlay open and focused; re-render so recent/trending show.
    this.refs.searchInput.focus();
    this.renderDropdown();
  }

  /**
   * Configure the leading button. On /a/search it is a back arrow that
   * returns to the site; on every other page it is an X that closes the
   * search overlay back to the normal header.
   */
  setupLeadButton() {
    const onSearchPage = this.isSearchPage();
    this.refs.leadButton.innerHTML = onSearchPage ? LEAD_ICON_BACK : LEAD_ICON_CLOSE;
    this.refs.leadButton.setAttribute(
      'aria-label',
      onSearchPage ? 'Back to site' : 'Close search'
    );
  }

  /**
   * Handle leading button click
   */
  handleLead(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isSearchPage()) {
      // Return to the site. Prefer history so the user lands where they
      // were; fall back to the locale home page on a cold/direct load.
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = this.dataset.homeUrl || '/';
      }
      return;
    }

    // Normal page: collapse the overlay back to the header.
    this.collapse();
    this.refs.searchInput.blur();
  }

  /**
   * Handle click outside (runs in capture phase)
   */
  handleClickOutside(event) {
    // Check if click is inside the component OR if the component is inside the clicked element
    const isInsideComponent = this.contains(event.target);
    const isClickOnComponentOrParent = event.target.contains(this);
    const dropdownIsVisible = !this.refs.searchDropdown.hidden;

    // If clicking inside the component OR on a parent element that contains the component, allow it
    if (isInsideComponent || isClickOnComponentOrParent) {
      return;
    }

    // Clicking outside - collapse the dropdown but let the click propagate
    // so the target element (cart icon, nav link, etc.) still receives it
    if (this.isExpanded && dropdownIsVisible) {
      this.collapse();
    }
  }

  /**
   * Navigate to search results page
   * @param {string} query - The search query
   * @param {object} options - Navigation options
   * @param {string} options.source - Source of the search (form_submit, suggestion, recent, trending)
   * @param {number} options.position - Position in suggestion list (0-indexed)
   */
  navigateToSearch(query, options = {}) {
    if (!query || query.trim().length === 0) return;

    const { source = 'form_submit', position = null } = options;

    // Update input to show selected query
    this.refs.searchInput.value = query.trim();

    // Add to recent searches
    this.addToRecentSearches(query);

    // Fire analytics event
    this.trackSearchSubmitted(query, source, position);

    // Brief delay to show the updated input before navigation
    setTimeout(() => {
      const encodedQuery = encodeURIComponent(query.trim());
      const searchUrl = this.dataset.searchUrl || '/a/search';
      let href = `${searchUrl}?q=${encodedQuery}`;
      // Pass forYouEnabled to discover page when style-quiz A/B treatment is active
      if (document.documentElement.classList.contains('style-quiz-enabled')) {
        try {
          const hasQuiz = !!localStorage.getItem('fy_taste_session')
            || (JSON.parse(localStorage.getItem('acq-quiz-v1') || '{}').fingerprint);
          if (hasQuiz) href += '&forYouEnabled=true';
        } catch {}
      }
      window.location.href = href;
    }, 100);
  }

  /**
   * Track search submitted event
   * @param {string} query - The search query
   * @param {string} source - Source of search
   * @param {number} position - Position in list (if applicable)
   */
  trackSearchSubmitted(query, source, position) {
    if (typeof window.Shopify?.analytics?.publish !== 'function') {
      return;
    }

    const payload = {
      query: query.trim(),
      source: source
    };

    if (position !== null) {
      payload.position = position;
    }

    window.Shopify.analytics.publish('search_submitted', payload);
    document.dispatchEvent(new CustomEvent('search:submitted', { detail: payload }));
  }

  /**
   * Expand search input
   */
  expand() {
    if (this.isExpanded) {
      return; // Already expanded
    }

    this.isExpanded = true;
    this.classList.add('is-expanded');

    // Force reflow to ensure CSS takes effect immediately
    void this.offsetHeight;

    this.positionMobileDropdown();

    this.refs.searchDropdown.hidden = false;
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  }

  /**
   * Position the mobile dropdown directly below the fixed search bar.
   * No-op on desktop, where the dropdown is absolutely positioned.
   */
  positionMobileDropdown() {
    if (window.matchMedia('(min-width: 750px)').matches) return;
    const rect = this.refs.searchInput.getBoundingClientRect();
    this.refs.searchDropdown.style.setProperty('--dropdown-top', Math.round(rect.bottom + 8) + 'px');
  }

  /**
   * Collapse search input
   */
  collapse() {
    this.selectedIndex = -1;

    // On the search proxy page the bar is the only search UI, so it must
    // always stay expanded — only hide the dropdown. This prevents the
    // header from snapping back to its collapsed state (leaving a phantom
    // clear button) when a stray scroll or click event fires.
    if (this.isSearchPage()) {
      this.refs.searchDropdown.hidden = true;
      this.refs.searchInput.setAttribute('aria-expanded', 'false');
      return;
    }

    this.isExpanded = false;
    this.classList.remove('is-expanded');
    this.refs.searchDropdown.hidden = true;
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    // Hide the clear button — the collapsed input has no room for it, and
    // leaving it visible produces a floating "X" next to the header icons.
    this.refs.clearButton.style.display = 'none';
  }

  /**
   * Render dropdown content
   */
  renderDropdown() {
    if (!this.isExpanded) return;

    const query = this.refs.searchInput.value.trim();
    const hasQuery = query.length >= MIN_QUERY_LENGTH;

    const sections = [];

    // Recent searches section
    if (!hasQuery && this.recentSearches.length > 0) {
      sections.push(this.renderRecentSearches());
    }

    // Autocomplete suggestions section
    if (hasQuery && this.autocompleteSuggestions.length > 0) {
      sections.push(this.renderAutocompleteSuggestions());
    }

    // If user has typed a query but no suggestions, show their query
    if (hasQuery && this.autocompleteSuggestions.length === 0) {
      sections.push(this.renderUserQuery(query));
    }

    // Trending searches section
    if (!hasQuery && this.trendingSearches.length > 0) {
      sections.push(this.renderTrendingSearches());
    }

    // Update dropdown content
    const dropdownContent = this.refs.searchDropdown.querySelector('[ref="dropdownContent"]');
    if (dropdownContent) {
      if (sections.length > 0) {
        dropdownContent.innerHTML = sections.join('');
        this.attachDropdownEventListeners();

        // Track suggestion impressions
        this.trackSuggestionsViewed(query, hasQuery);
      } else {
        dropdownContent.innerHTML = '<div class="autocomplete-search__empty">Start typing to search...</div>';
      }
    }
  }

  /**
   * Track suggestions viewed event
   * @param {string} query - Current search query
   * @param {boolean} hasQuery - Whether user has typed a query
   */
  trackSuggestionsViewed(query, hasQuery) {
    if (typeof window.Shopify?.analytics?.publish !== 'function') {
      return;
    }

    const suggestions = [];

    if (hasQuery && this.autocompleteSuggestions.length > 0) {
      suggestions.push({
        type: 'autocomplete',
        items: this.autocompleteSuggestions.map(s => s.query)
      });
    }

    if (!hasQuery && this.recentSearches.length > 0) {
      suggestions.push({
        type: 'recent',
        items: this.recentSearches.slice(0, 12).map(s => s.query)
      });
    }

    if (!hasQuery && this.trendingSearches.length > 0) {
      suggestions.push({
        type: 'trending',
        items: this.trendingSearches.slice(0, 6).map(s => s.query)
      });
    }

    if (suggestions.length > 0) {
      window.Shopify.analytics.publish('search_suggestions_viewed', {
        query: query || '',
        suggestions: suggestions
      });
    }
  }

  /**
   * Render recent searches section
   */
  renderRecentSearches() {
    return `
      <div class="autocomplete-search__section">
        <div class="autocomplete-search__section-header">
          <span class="autocomplete-search__section-icon">
            <svg width="16" height="16" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M13.0864 0C8.07503 0 3.84375 3.39701 2.57165 8.00921H0.645623C0.526174 8.00991 0.409233 8.04355 0.307662 8.10641C0.20609 8.16927 0.123824 8.25893 0.0699112 8.36552C0.0159982 8.47211 -0.00747248 8.59151 0.00208045 8.71058C0.0116334 8.82965 0.0538399 8.94377 0.124058 9.04041L2.88527 12.8175C2.94553 12.9002 3.0245 12.9675 3.11573 13.014C3.20696 13.0604 3.30788 13.0846 3.41024 13.0846C3.5126 13.0846 3.61352 13.0604 3.70475 13.014C3.79598 12.9675 3.87495 12.9002 3.93521 12.8175L6.69131 9.04041C6.76161 8.94366 6.80383 8.82937 6.81331 8.71016C6.8228 8.59094 6.79918 8.47142 6.74506 8.36477C6.69094 8.25812 6.60843 8.16849 6.50661 8.10575C6.4048 8.04301 6.28763 8.0096 6.16804 8.00921H4.3903C5.59854 4.36424 9.02842 1.74536 13.0864 1.74536C18.1574 1.74536 22.2529 5.8341 22.2529 10.9051C22.2529 15.9761 18.1574 20.0665 13.0864 20.0665C10.0644 20.0665 7.23981 18.577 5.53057 16.0849C5.39951 15.8946 5.19833 15.7641 4.97115 15.7219C4.74397 15.6797 4.50934 15.7293 4.31871 15.8599C4.22377 15.9248 4.14261 16.0079 4.07987 16.1043C4.01714 16.2007 3.97409 16.3085 3.95318 16.4216C3.93227 16.5347 3.93392 16.6508 3.95804 16.7633C3.98216 16.8757 4.02828 16.9823 4.09373 17.0769C6.12735 20.042 9.49092 21.8119 13.0864 21.8119C19.1006 21.8119 24 16.9193 24 10.9051C24 4.89085 19.1006 0 13.0864 0ZM12.4813 5.37925C12.3667 5.37969 12.2533 5.40271 12.1476 5.44698C12.0419 5.49124 11.946 5.5559 11.8652 5.63725C11.7845 5.71861 11.7206 5.81506 11.6772 5.92111C11.6337 6.02716 11.6116 6.14073 11.6121 6.25533V11.6533C11.612 11.7954 11.6465 11.9353 11.7128 12.0609C11.779 12.1866 11.8749 12.2941 11.9921 12.3743L14.9289 14.3787C15.1198 14.5091 15.3545 14.5585 15.5817 14.516C15.8089 14.4735 16.01 14.3426 16.1408 14.1521C16.2706 13.9613 16.3197 13.727 16.2772 13.5002C16.2347 13.2734 16.1042 13.0727 15.9141 12.9419L13.3574 11.1897V6.25533C13.3579 6.14016 13.3355 6.02603 13.2916 5.91954C13.2478 5.81304 13.1833 5.71629 13.1018 5.63484C13.0204 5.5534 12.9236 5.48889 12.8171 5.44502C12.7106 5.40115 12.5965 5.3788 12.4813 5.37925Z" fill="currentColor"/>
            </svg>
          </span>
          <span class="autocomplete-search__section-title">Recent searches</span>
          <button type="button" class="autocomplete-search__clear-all" data-action="clear-all">Clear all</button>
        </div>
        <div class="autocomplete-search__items" role="listbox">
          ${this.recentSearches.map((item, index) => `
            <div
              class="autocomplete-search__item"
              role="option"
              id="autocomplete-recent-${index}"
              data-query="${this.escapeHtml(item.query)}"
              data-action="search"
              data-suggestion-type="recent"
              data-position="${index}"
            >
              <span class="autocomplete-search__item-text">${this.escapeHtml(item.query)}</span>
              <button
                type="button"
                class="autocomplete-search__item-remove"
                data-action="remove-recent"
                data-query="${this.escapeHtml(item.query)}"
                aria-label="Remove ${this.escapeHtml(item.query)}"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="16" height="16">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 5 5 15M5 5l10 10"/>
                </svg>
              </button>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  /**
   * Render autocomplete suggestions section
   */
  renderAutocompleteSuggestions() {
    return `
      <div class="autocomplete-search__section">
        <div class="autocomplete-search__items" role="listbox">
          ${this.autocompleteSuggestions.map((item, index) => `
            <div
              class="autocomplete-search__item"
              role="option"
              id="autocomplete-suggestion-${index}"
              data-query="${this.escapeHtml(item.query)}"
              data-action="search"
              data-suggestion-type="autocomplete"
              data-position="${index}"
            >
              <span class="autocomplete-search__item-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="16" height="16">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m19 19-4.35-4.35M17 9A8 8 0 1 1 1 9a8 8 0 0 1 16 0Z"/>
                </svg>
              </span>
              <span class="autocomplete-search__item-text">${item.highlightedQuery}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  /**
   * Render trending searches section
   */
  renderTrendingSearches() {
    return `
      <div class="autocomplete-search__section">
        <div class="autocomplete-search__section-header">
          <span class="autocomplete-search__section-icon">
            <svg width="12" height="26" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.94043 8.41699C9.94056 8.60666 10.016 8.78733 10.1484 8.9209L10.1494 8.92285C10.2836 9.05705 10.4656 9.13178 10.6553 9.13184H17.1885C17.1979 9.13191 17.2068 9.13482 17.2148 9.13965L17.2334 9.15918C17.2376 9.16737 17.2398 9.17666 17.2393 9.18652C17.2387 9.19554 17.2356 9.20424 17.2305 9.21191L7.20703 23.1504V15.3408C7.20702 15.1513 7.13103 14.9691 6.99707 14.835C6.8626 14.7002 6.68045 14.625 6.49121 14.625H0.541992L0.527344 14.626C0.522892 14.6262 0.518159 14.6255 0.513672 14.623L0.50293 14.6123C0.500718 14.608 0.49962 14.6022 0.5 14.5967C0.500369 14.5919 0.502159 14.5869 0.504883 14.583L0.509766 14.5762L0.513672 14.5703L9.94043 0.874023V8.41699Z" fill="currentColor" stroke="currentColor"/>
            </svg>
          </span>
          <span class="autocomplete-search__section-title">Trending searches</span>
        </div>
        <div class="autocomplete-search__items" role="listbox">
          ${this.trendingSearches.slice(0, 6).map((item, index) => `
            <div
              class="autocomplete-search__item"
              role="option"
              id="autocomplete-trending-${index}"
              data-query="${this.escapeHtml(item.query)}"
              data-action="search"
              data-suggestion-type="trending"
              data-position="${index}"
            >
              <span class="autocomplete-search__item-text">${this.escapeHtml(item.query)}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  /**
   * Render user's typed query when no suggestions are found
   */
  renderUserQuery(query) {
    return `
      <div class="autocomplete-search__section">
        <div class="autocomplete-search__items" role="listbox">
          <div
            class="autocomplete-search__item"
            role="option"
            id="autocomplete-user-query"
            data-query="${this.escapeHtml(query)}"
            data-action="search"
            data-suggestion-type="user_query"
            data-position="0"
          >
            <span class="autocomplete-search__item-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="16" height="16">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m19 19-4.35-4.35M17 9A8 8 0 1 1 1 9a8 8 0 0 1 16 0Z"/>
              </svg>
            </span>
            <span class="autocomplete-search__item-text">${this.escapeHtml(query)}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Attach event listeners to dropdown items
   */
  attachDropdownEventListeners() {
    // Search items
    this.refs.searchDropdown.querySelectorAll('[data-action="search"]').forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const query = item.dataset.query;
        const suggestionType = item.dataset.suggestionType;
        const position = item.dataset.position ? parseInt(item.dataset.position, 10) : null;
        this.navigateToSearch(query, { source: suggestionType || 'suggestion', position });
      });
    });

    // Remove recent search
    this.refs.searchDropdown.querySelectorAll('[data-action="remove-recent"]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const query = button.dataset.query;
        this.removeRecentSearch(query);
      });
    });

    // Clear all
    const clearAllButton = this.refs.searchDropdown.querySelector('[data-action="clear-all"]');
    if (clearAllButton) {
      clearAllButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.clearAllRecentSearches();
        // Keep input focused to prevent dropdown from closing
        this.refs.searchInput.focus();
      });
    }
  }

  /**
   * Escape HTML for safe rendering
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Register custom element
if (!customElements.get('autocomplete-search-component')) {
  customElements.define('autocomplete-search-component', AutocompleteSearchComponent);
}

export default AutocompleteSearchComponent;
